'use client'

import { useAtomValue } from 'jotai'
import { selectAtom } from 'jotai/utils'
import { useMemo } from 'react'

import { collapsibleValidationError } from '@/features/order/order-form/lib/collapsible-validation'
import { withoutExitBracket } from '@/features/order/order-form/lib/exit-active'
import { safeParseFloat } from '@/lib/utils'
import { findTickerBySymbol, watchListTickerMapAtom } from '@/state/dashboard'
import { orderFormCollapsibleConfigAtomFamily } from '@/state/order'

interface UseCollapsibleValidationArgs {
  cardId: string
  /** Symbols of the current order items, used to read a reference price for exit checks. */
  symbols: string[]
  /** Entry side of the order (single-leg); flips the valid TP/SL direction for shorts. */
  side?: 'buy' | 'sell'
  /** Exactly one distinct ticker (row-group-key.ts identity) — the exit bracket only
   * applies to a single instrument, regardless of how many accounts back that one row. */
  isSingleVisualRow: boolean
  /** That single row's accounts aren't all on the same side — the bracket can't apply
   * a single Long-relative TP/SL to a row that's simultaneously Long and Short. */
  hasMixedSides: boolean
  isTwapMode: boolean
  isAdvancedTwapMode: boolean
  isScaleMode: boolean
  isLimitChaseMode: boolean
  isSingleHedgeMode: boolean
  isSpreaderMode: boolean
}

/**
 * Computes the first blocking error for the Order Trigger / Exit sections so the
 * submit button can disable and explain why. Subscribes only to the first known
 * last-price among the order symbols (via selectAtom) so it re-renders on a
 * relevant price move, not every tick.
 */
export function useCollapsibleValidation({
  cardId,
  symbols,
  side,
  isSingleVisualRow,
  hasMixedSides,
  isTwapMode,
  isAdvancedTwapMode,
  isScaleMode,
  isLimitChaseMode,
  isSingleHedgeMode,
  isSpreaderMode,
}: UseCollapsibleValidationArgs): string | null {
  // Read the shared atom directly — NOT useOrderFormCollapsibleConfig, whose
  // local state only applies the atom once and goes stale. The submit handler
  // reads this same atom, so the gate and the click path can never disagree.
  const stored = useAtomValue(orderFormCollapsibleConfigAtomFamily(cardId))
  const config = useMemo(() => withoutExitBracket(stored), [stored])

  const symbolKey = symbols.join(',')
  const priceAtom = useMemo(
    () =>
      selectAtom(watchListTickerMapAtom, (map) => {
        for (const sym of symbols) {
          const p = safeParseFloat(findTickerBySymbol(map, sym)?.lastPrice || '')
          if (p) return p
        }
        return 0
      }),
    // symbolKey captures the symbol set; selectAtom de-dupes by the returned price.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [symbolKey]
  )
  const currentPrice = useAtomValue(priceAtom)

  return useMemo(
    () =>
      collapsibleValidationError(config, {
        // The bracket can't span a basket, and can't apply a single
        // Long-relative TP/SL to a row that's simultaneously Long and Short.
        applicable:
          !isTwapMode &&
          !isAdvancedTwapMode &&
          !isScaleMode &&
          !isLimitChaseMode &&
          !isSingleHedgeMode &&
          !isSpreaderMode &&
          isSingleVisualRow &&
          !hasMixedSides,
        currentPrice: currentPrice || undefined,
        side,
      }),
    [
      config,
      isTwapMode,
      isAdvancedTwapMode,
      isScaleMode,
      isLimitChaseMode,
      isSingleHedgeMode,
      isSpreaderMode,
      isSingleVisualRow,
      hasMixedSides,
      currentPrice,
      side,
    ]
  )
}
